import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';

export interface BulkConfirmationCounts {
  totalTargets: number;
  updatedCount: number;
  createdCount: number;
  removedCount: number;
}

interface DialogState {
  counts: BulkConfirmationCounts;
  resolve: (value: boolean) => void;
}

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

export function useBulkConfirmationDialog() {
  const [dialogState, setDialogState] = React.useState<DialogState | null>(null);

  const closeDialog = React.useCallback((result: boolean) => {
    setDialogState((prev) => {
      prev?.resolve(result);
      return null;
    });
  }, []);

  const confirmBulkChanges = React.useCallback(
    (counts: BulkConfirmationCounts) =>
      new Promise<boolean>((resolve) => {
        setDialogState({ counts, resolve });
      }),
    [],
  );

  const dialogElement = dialogState ? (
    <Dialog open onClose={() => closeDialog(false)} fullWidth maxWidth="sm">
      <DialogTitle>Confirm Updates</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Update {pluralize(dialogState.counts.totalTargets, 'slide', 'slides')} across selected
          locations?
        </Typography>
        {dialogState.counts.updatedCount ? (
          <Typography gutterBottom>
            {pluralize(
              dialogState.counts.updatedCount,
              'property requires an update',
              'properties require updates',
            )}
            .
          </Typography>
        ) : null}
        {dialogState.counts.createdCount ? (
          <Typography gutterBottom>
            Adding to {pluralize(
              dialogState.counts.createdCount,
              'additional property',
              'additional properties',
            )}.
          </Typography>
        ) : null}
        {dialogState.counts.removedCount ? (
          <Typography gutterBottom>
            Removing from {pluralize(
              dialogState.counts.removedCount,
              'property',
              'properties',
            )}.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => closeDialog(false)} color="inherit">
          Cancel
        </Button>
        <Button onClick={() => closeDialog(true)} variant="contained" color="primary">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  ) : null;

  return { confirmBulkChanges, dialogElement };
}
